import React, { useRef, useEffect, useMemo } from "react";
import styles from "../modal-options/index.module.scss";
import OptionRow from "../modal-options/option-row";
import InputText from "../modal-options/input-text.component";
import AIReactQuill from "../../../../Common/ai-react-quill";
import { TextAreaFormats, TextAreaModules } from "../../../../../constants/textarea.constants";
import ColorPickerDropdown from "../../../tags/tags-table/color-picker-dropdown.component";
import cn from "../../../../../Utils/class-names.utils";
import { FormikProps } from "formik";
import { IEmailTemplateFormValues } from "../../../../../common/validations/email-templates.validations";

interface FormContentProps {
  formikProps: FormikProps<IEmailTemplateFormValues>;
}

const InviteOptionsForm = ({ formikProps }: FormContentProps) => {
  const { values, setFieldValue } = formikProps;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const logoPreviewUrl = useMemo(() => {
    if (!values.logo) return undefined;
    if (typeof values.logo === 'string') return values.logo;
    return URL.createObjectURL(values.logo);
  }, [values.logo]);

  useEffect(() => {
    const url = logoPreviewUrl;
    const isFile = values.logo instanceof File;
    return () => {
      if (url && isFile) {
        URL.revokeObjectURL(url);
      }
    };
  }, [logoPreviewUrl, values.logo]);

  const handleLogoChange = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue('logo', file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLogoRemove = () => {
    setFieldValue('logo', undefined);
  };

  return (
    <div className={cn(styles.content, "transparent-scroller overflow-x-hidden")}>
      <OptionRow label="Subject">
        <InputText 
          value={values.subject} 
          onChange={(e) => setFieldValue('subject', e.target.value)}
        />
      </OptionRow>
      <OptionRow label="Header">
        <AIReactQuill
          theme="snow"
          value={values.header}
          onChange={(e: any) => {
            setFieldValue('header', e);
          }}
          onUpdate={(value: any) => {
            setFieldValue('header', value);
          }}
          modules={TextAreaModules}
          formats={TextAreaFormats}
          bounds={".app"}
        />
      </OptionRow>
      <OptionRow label="Body" className={styles.borderBottom}>
        <div className="d-flex flex-column gap-4">
          <AIReactQuill
            theme="snow"
            value={values.body}
            onChange={(e: any) => {
              setFieldValue('body', e);
            }}
            onUpdate={(value: any) => {
              setFieldValue('body', value);
            }}
            modules={TextAreaModules}
            formats={TextAreaFormats}
            bounds={".app"}
          />
          <div className="d-flex align-items-center justify-content-between gap-16">
            <h3 className="m-0 f-12 line-height-18 f-400 f-black">
              Text Colour
            </h3>
            <div className="p-1">
              <ColorPickerDropdown
                color={values.textColor}
                isDisabled={false}
                onColorSelect={(color: string) => setFieldValue('textColor', color)}
                popperConfig={{ strategy: 'fixed' }}
                renderOnMount={true}
              />
            </div>
          </div>
        </div>
      </OptionRow>
      <OptionRow className={styles.logoWrapper}>
        <section className={styles.logoSection}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="d-none"
            aria-label="Logo file input"
          />
          <div className={styles.logoContainer}>
            <div
              className={styles.logoPreview}
              role="img"
              aria-label="Logo preview"
              style={logoPreviewUrl ? { backgroundImage: `url(${logoPreviewUrl})` } : undefined}
            />

            <div className={styles.logoInfo}>
              <div className={styles.logoLabel}>
                Logo
              </div>

              <p className={styles.logoDescription}>
                Recommended image size 256 x 256 pixels
              </p>

              <div className={styles.changeButtonWrapper}>
                <button
                  className={cn(styles.changeButton, "my-button")}
                  onClick={handleLogoChange}
                  type="button"
                  aria-label={values.logo ? "Change logo" : "Select logo"}
                >
                  {values.logo ? 'Change' : 'Select'}
                </button>
                {values.logo && (
                  <button
                    className={cn(styles.changeButton, styles.removeButton, "my-button")}
                    onClick={handleLogoRemove}
                    type="button"
                    aria-label="Remove logo"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className={styles.colorPickerRow}>
            <label
              htmlFor="background-color-picker"
              className={styles.colorPickerLabel}
            >
              Background Colour
            </label>

            <ColorPickerDropdown
              color={values.backgroundColor}
              isDisabled={false}
              onColorSelect={(color: string) => setFieldValue('backgroundColor', color)}
              popperConfig={{ strategy: 'fixed' }}
              renderOnMount={true}
              fetchDataOnMount={false}
            />
          </div>

          <div className={styles.colorPickerRow}>
            <label
              htmlFor="button-color-picker"
              className={styles.colorPickerLabel}
            >
              Button Colour
            </label>

            <ColorPickerDropdown
              color={values.buttonColor}
              isDisabled={false}
              onColorSelect={(color: string) => setFieldValue('buttonColor', color)}
              popperConfig={{ strategy: 'fixed' }}
              renderOnMount={true}
              fetchDataOnMount={false}
            />
          </div>
        </section>
      </OptionRow>
    </div>
  );
};

export default InviteOptionsForm;

